
    import Vue from 'vue';
    import Component from "vue-class-component";
    import HeadingWave from "@/features/shared/components/HeadingWave.vue";
    import {
        factory, OrganizationModel, RepresentativeModel,
        ResultError,
        SysAdminMedicalPractitionersClient,
        UpsertMedicalPractitionerRequest,
        MedicalPractitionerModel
    } from "@/api-client";
    import toasts from "@/components/Toasts";
    import LabelOptional from "@/features/shared/components/LabelOptional.vue";
    import { BvTableFieldArray } from "bootstrap-vue";
    import AdministrationDetailviewLayout from "@/features/administration/AdministrationDetailviewLayout.vue";
    import {Prop} from "vue-property-decorator";
    import {NavigationGuardNext} from "vue-router/types/router";
    import TrueFalseOptions from "@/features/shared/TrueFalseOptions";
    import {translateTrueFalse} from "@/features/shared/TrueFalseTranslator";
    import {stringOrEmptyPlaceholder} from "@/extensions/string.helpers";
    import ErrorsSummary from "@/components/ErrorsSummary.vue";

    interface Props {
        model?: UpsertMedicalPractitionerRequest;
        organizationOptions: OrganizationModel[];
        existingPractisioners?: MedicalPractitionerModel[];
        previouslyLinkedRepresentative: { representative: RepresentativeModel, organizationId: string } | null;
    }

    @Component({
        components: {
            HeadingWave,
            LabelOptional,
            AdministrationDetailviewLayout,
            ErrorsSummary
        },
        async beforeRouteEnter(to, from, next: NavigationGuardNext<UpsertMedicalPractitioner>) {
            const props = to.meta!.props as Props;
            try {
                props.previouslyLinkedRepresentative = null;

                if (to.params.id) {
                    const paramPractisionerView = await new SysAdminMedicalPractitionersClient().get(to.params.id);
                    const practitionerToUpdate = paramPractisionerView.medicalPractitionerModels.find(x => x.id == to.params.id);
                        if (practitionerToUpdate != null) {
                        props.model = new UpsertMedicalPractitionerRequest({
                            id: practitionerToUpdate.id,
                            firstName: practitionerToUpdate.firstName,
                            lastName: practitionerToUpdate.lastName,
                            organizationId: practitionerToUpdate.organizationId,
                            representativeId: practitionerToUpdate.representative?.id,
                            isActive: practitionerToUpdate.isActive
                        });

                        if (practitionerToUpdate.representative) {
                            props.previouslyLinkedRepresentative = {
                                representative: practitionerToUpdate.representative,
                                organizationId: practitionerToUpdate.organizationId
                              };
                            }
                        }
                        props.existingPractisioners = paramPractisionerView.medicalPractitionerModels
                }

                props.organizationOptions = await new SysAdminMedicalPractitionersClient().organizationOptions();
                next();
            }
            catch (e) {
                next(e)
            }
        }
    })
  export default class UpsertMedicalPractitioner extends Vue implements Props {
    @Prop({ required: false, default: () => factory.create(UpsertMedicalPractitionerRequest) })
    model!: UpsertMedicalPractitionerRequest;

    @Prop({ required: true })
    organizationOptions!: OrganizationModel[];

    // Since representative options returns only representatives not linked to a practitioner we have to provide previously linked practitioner manually.
    // This will enable the user to select the same practitioner again when updating.
    @Prop({ required: false, default: () => null })
    previouslyLinkedRepresentative!: { representative: RepresentativeModel, organizationId: string } | null;

    representativeOptions: { displayName: string, id: string }[] = [];
    yesNoOptions = TrueFalseOptions.options;
    validationErrors: ResultError = ResultError.createEmpty();

    @Prop({ required: false, default: () => [] })
    existingPractisioners!: MedicalPractitionerModel[];


    selectedOrganizations: string[] = [];

    fields: BvTableFieldArray = [
        {
            key: "id",
            label: this.$ls.Global_Label_FullName,
            sortable: false,
            sortByFormatted: true,
            formatter: (value: any, key: any, item: MedicalPractitionerModel): string => `${item.firstName} ${item.lastName}`
        },
        {
            key: "organizationName",
            label: this.$ls.Administration_MedicalPractitioners_Table_Label_Organization,
            sortable: true,
        },
        {
            key: "representativeFullName",
            label: this.$ls.Administration_MedicalPractitioners_Table_Label_LinkedRepresentative,
            sortable: false,
            formatter: (value: any, key: any, item: MedicalPractitionerModel): string => stringOrEmptyPlaceholder(item.representative ? `${item.representative?.fullName} (${item.representative?.usernameOrPersonalCode})`:null)
        },
        {
            key: "isActive",
            label: this.$ls.Global_Label_Active,
            sortable: false,
            sortByFormatted: true,
            formatter: (value: any, key: any, item: MedicalPractitionerModel): string => translateTrueFalse(item.isActive)
        },
        {
            key: "linkIcon",
            label: "",
            sortable: false,
            tdClass:"table-actions"
        },
    ];

    async created() {
        this.model.organizationId = this.model.organizationId || null;

        this.setRepresentativeOptions();

        if (this.previouslyLinkedRepresentative)
            this.model.representativeId = this.previouslyLinkedRepresentative!.representative.id;
    }

    async save() {
        this.validationErrors.clear();

        await new SysAdminMedicalPractitionersClient().upsert(this.model)
            .then(
                async (result) => {
                    toasts.success(this.$ls.Administration_MedicalPractitioners_Toast_Success_MedicalPractitionerSaved);
                    if (this.$route.params.id == result.value) {
                        this.fillData(this.$route.params.id);
                    }
                    else {
                        this.$router.push({ name: "edit-medical-practitioner", params: { id: result.value } });
                        this.fillData(result.value);
                    }
                },
                (errorResponse: ResultError) => {
                    this.validationErrors = errorResponse;
                    toasts.failure(this.$ls.Global_Toast_ActionFailureWithErrorsAbove);
                });
    }

    setRepresentativeOptions() {
        if (!this.model.organizationId) {
            return;
        }

        this.representativeOptions = this.organizationOptions.find(x => x.id == this.model.organizationId)!.representatives
            .map(x => { return { id: x.id, displayName: this.getRepresentativeDisplayName(x) } });

        this.model.representativeId = null;
    }

    getRepresentativeDisplayName(representative: RepresentativeModel) {
        return `${representative.fullName} (${representative.usernameOrPersonalCode})`;
    }
    selectEdit(practitionerId:string){
        const selectedModel = this.existingPractisioners.find(x=>x.id==practitionerId)!;
        this.model = new UpsertMedicalPractitionerRequest({
            id: selectedModel.id,
            firstName: selectedModel.firstName,
            lastName: selectedModel.lastName,
            organizationId: selectedModel.organizationId,
            representativeId: selectedModel.representative?.id,
            isActive: selectedModel.isActive
        })
        if (selectedModel.representative) {
            this.previouslyLinkedRepresentative = {
                representative: selectedModel.representative,
                organizationId: selectedModel.organizationId
            };
        } else {
            this.previouslyLinkedRepresentative = null;
        }
        this.setRepresentativeOptions();
        if (this.previouslyLinkedRepresentative)
            this.model.representativeId = this.previouslyLinkedRepresentative!.representative.id;
    }

        addNew() {
        if (this.model) {
            this.model = new UpsertMedicalPractitionerRequest({
                id:null,
                firstName: this.model.firstName,
                lastName: this.model.lastName,
                organizationId: null,
                representativeId: null,
                isActive: this.model.isActive
            })
        } else {
            this.model = factory.create(UpsertMedicalPractitionerRequest);
        }
        this.previouslyLinkedRepresentative = null;
        this.setRepresentativeOptions();
    }

    rowClass(item: MedicalPractitionerModel, type: string) {
        if (!item || type !== 'row') return
        if (item.id == this.model.id) return 'table-success'
    }

    async fillData(practitionerId: string) {
        this.previouslyLinkedRepresentative = null;
        const paramPractisionerView = await new SysAdminMedicalPractitionersClient().get(practitionerId);
        this.existingPractisioners = paramPractisionerView.medicalPractitionerModels;
        const practitionerToUpdate = paramPractisionerView.medicalPractitionerModels.find(x => x.id == practitionerId);
        if (practitionerToUpdate != null) {
            this.model = new UpsertMedicalPractitionerRequest({
                id: practitionerToUpdate.id,
                firstName: practitionerToUpdate.firstName,
                lastName: practitionerToUpdate.lastName,
                organizationId: practitionerToUpdate.organizationId,
                representativeId: practitionerToUpdate.representative ? practitionerToUpdate.representative.id : null,
                isActive: practitionerToUpdate.isActive
            });

            if (practitionerToUpdate.representative) {
                this.previouslyLinkedRepresentative = {
                    representative: practitionerToUpdate.representative,
                    organizationId: practitionerToUpdate.organizationId
                };
            }
            this.setRepresentativeOptions();
        }
        if (this.previouslyLinkedRepresentative)
            this.model.representativeId = this.previouslyLinkedRepresentative!.representative.id;
    }
}
